/* eslint-disable @typescript-eslint/no-explicit-any */
import { STRINGS } from 'config/constants';
import { format, isValid, parse } from 'date-fns';
import { FailedSaveToRecord, SAVING, SuccessfulSaveToRecord } from 'model/toolbar/saveToRecord';
import { Template } from 'store/reducer/templates';
import styles from '../styles/theme';

const successStyles = {
  bgColor: styles.colorStatusSuccessBg,
  textColor: styles.colorStatusSuccess,
  borderColor: styles.colorStatusSuccess,
  accessibleLabel: 'Patient is matched, click to view patient details',
};
const warningStyles = {
  bgColor: styles.colorStatusWarningBg,
  textColor: styles.colorStatusWarning,
  borderColor: styles.colorStatusWarning,
};
const errorStyles = {
  bgColor: styles.colorStatusErrorBg,
  textColor: styles.colorStatusError,
  borderColor: styles.colorStatusError,
};
const infoStyles = {
  bgColor: styles.colorStatusInfoBg,
  textColor: styles.colorStatusInfo,
  borderColor: styles.colorStatusInfo,
};

export function formatNameDobString(name: string, dateOfBirth: string) {
  if (!dateOfBirth) return '';
  let parsedDate;
  // Detect and parse ISO date (yyyy-MM-dd)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
    parsedDate = parse(dateOfBirth, 'yyyy-MM-dd', new Date());
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOfBirth)) {
    // Detect and parse UK style date (dd/MM/yyyy)
    parsedDate = parse(dateOfBirth, 'dd/MM/yyyy', new Date());
  } else {
    return ''; // Unknown format
  }

  if (!isValid(parsedDate)) return '';

  const formattedDate = format(parsedDate, 'dd/MM/yyyy');
  return `${name} (${formattedDate})`;
}

// interface MatchData {
//   nhsNumber: string | null;
//   gender: string | null;
//   dateOfBirth: string | null;
//   firstName: string | null;
//   surname: string | null;
//   postcode: string | null;
//   patientIdAtPractice: string | null;
// }

export const getButtonProps = (
  isPatientMatched: boolean,
  saveToRecordState: string,
  matchState: string,
  matchData: any,
) => {
  const name = matchData && `${matchData.firstName[0]} ${matchData.surname}`;
  if (saveToRecordState === 'NOT_SAVED' && isPatientMatched) {
    return {
      ...infoStyles,
      text: 'Matched',
      supportText: formatNameDobString(name, matchData?.dateOfBirth),
      showReloadIcon: true,
    };
  }
  if (saveToRecordState === 'SAVING') {
    return { ...successStyles, text: 'Saving...' };
  }
  if (saveToRecordState === 'SAVED' || saveToRecordState === 'LEGACY_SAVED') {
    return { ...successStyles, text: 'Saved', supportText: `Successfully saved to ${name}’s record` };
  }
  if (saveToRecordState === 'PARTIALLY_SAVED') {
    return { ...warningStyles, text: 'Partially saved', accessibleLabel: successStyles.accessibleLabel };
  }
  if (matchState === 'NO_MATCH_RESULTS_FOUND') {
    return {
      ...warningStyles,
      text: 'No results',
      accessibleLabel: 'No matching patient found, click here to match manually',
    };
  }
  if (!isPatientMatched && matchState === 'MATCHING_ERROR') {
    return {
      ...errorStyles,
      text: 'Error',
      supportText: 'Couldn’t get current open patient',
      showReloadIcon: true,
      accessibleLabel: 'Patient matching error, click here to match manually',
    };
  }
  return {
    bgColor: styles.colorNeutralNeutral9,
    textColor: styles.colorNeutralNeutral3,
    borderColor: styles.colorNeutralNeutral3,
    text: 'Not matched',
    supportText: 'Getting open patient record',
    accessibleLabel: 'Consultation is not matched to a patient, click to match',
  };
};

const emptyPatient = {
  nhsNumber: null,
  gender: null,
  dateOfBirth: '',
  firstName: '',
  surname: '',
  postcode: null,
  patientIdAtPractice: null,
};

export function getSaveToRecordState(
  saveToRecordStatus: SuccessfulSaveToRecord | FailedSaveToRecord | typeof SAVING | null,
) {
  if (saveToRecordStatus === SAVING) {
    return 'SAVING';
  }
  if (saveToRecordStatus === null) {
    return 'NOT_SAVED';
  }
  if ('statusCode' in saveToRecordStatus && saveToRecordStatus?.statusCode === 400) {
    return 'NOT_SAVED';
  }
  if (saveToRecordStatus && 'timeStamp' in saveToRecordStatus) {
    return 'SAVED';
  }
  return '';
}

export function getPatientMatchingChip(patient: any, clinicalSystemName: string | null) {
  const patientStatusCheck = patient && typeof patient === 'object' && 'status' in patient;
  const isPatientMatched = patientStatusCheck ? patient.status === 200 : false;
  const isInteropEnabled = clinicalSystemName && ['EMIS', 'S1'].includes(clinicalSystemName);
  const checkForMatchingError = patientStatusCheck && patient?.status === 400 ? 'MATCHING_ERROR' : 'NOT_MATCHED';
  const patientMatchingChip = {
    isPatientMatched,
    saveToRecordState: 'ready',
    matchState: isPatientMatched ? 'NOT_SAVED' : checkForMatchingError,
    matchData: patient && typeof patient === 'object' && 'dateOfBirth' in patient ? patient : emptyPatient,
  };
  return {
    isInteropEnabled,
    isPatientMatched,
    patientMatchingChip,
  };
}

export const mockTemps: Template[] = [
  {
    id: 'f9d8121f-dd2c-4a6e-ba3e-4fb545dde9bd',
    name: 'Template A',
    practiceRole: 'GP',
    content: 'aaaa',
    type: 'SHARED',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: 'c5c2f46f-202b-49fd-8e41-8cf0ef679067',
    name: 'Template B',
    practiceRole: 'GP',
    content: 'bbbbb',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: 'c784e85d-7783-4b35-9688-4456743497b9',
    name: 'Default',
    practiceRole: 'Clinician',
    content:
      'Presenting Complaint\nHistory of Presenting Complaint\nPast Medical History Discussed' +
      '\nDrug History Discussed\nSocial History\nConcenrs and expectations\nInvestigations\nTreatment\nAdvice',
    type: 'SHARED',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: '84fc606c-3773-4209-814d-5bbc3b71c039',
    name: 'dsf',
    practiceRole: 'sdfdfsff',
    content: 'sdfsdfsdfdsf',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: 'ba0b62be-8c2d-4a47-a087-5543bf56ff70',
    name: 'sdf',
    practiceRole: 'sdfs',
    content: 'dfsdfsdfs',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: '81cbe655-caa9-41bf-9e85-67f4d305916b',
    name: 'sdfd',
    practiceRole: 'fsdfds',
    content: 'fsdfs',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: '09391e5b-394b-42ec-98fd-91fe2bba6c7b',
    name: 'shared3',
    practiceRole: 'shared2',
    content: 'shared1',
    type: 'SHARED',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: '2d8abe47-4511-46df-8da3-cb88dd411666',
    name: 'teasts',
    practiceRole: 'aasfafsa',
    content: 'fafasfafa',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
  {
    id: '93b77160-9000-4c28-8757-7330e49dfe90',
    name: 'tyvghbjn',
    practiceRole: 'jn',
    content: 'bkhjnk',
    type: 'PERSONAL',
    transcriptionType: 'Consultation' as const,
  },
];

type SectionsMap = {
  history: string | null;
  examination: string | null;
  procedure: string | null;
  allergy: string | null;
  familyHistory: string | null;
  summary: string | null;
  problem: null | string;
};

export function mapSectionsForEmisSaveToRecord(input: string): SectionsMap {
  const sectionMap: Record<string, keyof SectionsMap> = {
    history: 'history',
    examination: 'examination',
    procedure: 'procedure',
    'family history': 'familyHistory',
    allergy: 'allergy',
  };

  const result: SectionsMap = {
    history: null,
    examination: null,
    procedure: null,
    allergy: null,
    familyHistory: null,
    summary: null,
    problem: null,
  };

  const matches = Array.from(input.matchAll(/^([A-Za-z ]+)\n/gm));

  matches.forEach((match, i) => {
    const sectionTitle = match[1].trim().toLowerCase();
    const startIndex = (match.index as number) + match[0].length;
    const endIndex = i + 1 < matches.length ? (matches[i + 1].index as number) : input.length;
    const content = input.slice(startIndex, endIndex).trim();

    if (sectionMap[sectionTitle]) {
      const key = sectionMap[sectionTitle];
      result[key] = result[key] ? `${result[key]}\n${content}` : content;
    } else {
      const withTitle = `${match[1].trim()}\n${content}`;
      result.summary = result.summary ? `${result.summary}\n${withTitle}` : withTitle;
    }
  });

  return {
    ...result,
    summary: result.summary ? `${STRINGS.INTEROP.INITIAL_DRAFT_COPY} ${result.summary}` : null,
  };
}
